import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { FileDown, Loader2, ShoppingCart, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BestellungenExport = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      // Build query params
      const params = new URLSearchParams();
      
      if (selectedLocation !== 'all') {
        params.append('location_id', selectedLocation);
      }
      
      if (startDate) {
        params.append('start_date', startDate);
      }
      
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      if (status !== 'all') {
        params.append('status', status);
      }
      
      const queryString = params.toString();
      const url = `${API}/api/orders/export-pdf${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Bestellungen_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF erfolgreich erstellt!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Erstellen des PDFs');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setSelectedLocation('all');
    setStartDate('');
    setEndDate('');
    setStatus('all');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Bestellungen Export
            </h1>
            <p className="text-muted-foreground mt-2">
              Exportieren Sie Ihre Bestellungen als PDF
            </p>
          </div>
        </div>

        {/* Export Card */}
        <Card className="border-2">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>PDF Export Konfiguration</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Wählen Sie Filter für Ihren Export aus
                </p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Location Filter */}
                <div className="space-y-2">
                  <Label>Standort</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger>
                      <SelectValue placeholder="Standort wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Standorte</SelectItem>
                      {locations.map(location => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="processing">In Bearbeitung</SelectItem>
                      <SelectItem value="completed">Abgeschlossen</SelectItem>
                      <SelectItem value="cancelled">Storniert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label>Von Datum</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                  />
                </div>

                {/* End Date */}
                <div className="space-y-2">
                  <Label>Bis Datum</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                  />
                </div>
              </div>

              {/* Filter Summary */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <Filter className="h-4 w-4 text-blue-600" />
                <span className="text-sm">
                  {selectedLocation === 'all' && status === 'all' && !startDate && !endDate
                    ? 'Keine Filter aktiv - Alle Bestellungen werden exportiert'
                    : 'Filter aktiv'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  className="flex-1"
                  size="lg"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      PDF wird erstellt...
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-5 w-5" />
                      PDF Exportieren
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  disabled={exporting}
                  size="lg"
                >
                  Filter zurücksetzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2">💡 Hinweise zum Export</h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Der Export enthält Bestellnummer, Datum, Standort, Artikel und Gesamtbetrag</li>
              <li>Verwenden Sie die Filter, um gezielt bestimmte Bestellungen zu exportieren</li>
              <li>Das PDF wird automatisch heruntergeladen</li>
              <li>Maximale Anzahl: 1000 Bestellungen pro Export</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
